import type { Ref } from 'react';
import type { SmartIconProps } from './types';

type IconButtonProps = {
  src: string;
  alt: string;
  onClick?: () => void;
  onPointerDown?: () => void;
  onMouseDown?: () => void;
  buttonRef?: Ref<HTMLButtonElement>;
  size: number | string;
  iconSize?: number | string;
  buttonClassName?: string;
  iconClassName?: string;
  iconColor?: string;
  preserveSvgColor?: boolean;
};

/** Returns true when the asset URL points to an SVG file. */
function isSvg(url: string): boolean {
  return url.split('?')[0].toLowerCase().endsWith('.svg');
}

/** Renders a standardized icon button wrapper used across the audio player. */
export function IconButton({
  src,
  alt,
  onClick,
  onPointerDown,
  onMouseDown,
  buttonRef,
  size,
  iconSize,
  buttonClassName,
  iconClassName,
  iconColor,
  preserveSvgColor,
}: IconButtonProps): JSX.Element {
  return (
    <button
      type="button"
      ref={buttonRef}
      onClick={onClick}
      onPointerDown={onPointerDown}
      onMouseDown={onMouseDown}
      aria-label={alt}
      className={buttonClassName}
      style={{ width: size, height: size }}
    >
      <SmartIcon
        src={src}
        size={iconSize ?? size}
        alt={alt}
        color={iconColor}
        className={iconClassName}
        preserveSvgColor={preserveSvgColor}
      />
    </button>
  );
}

/**
 * SmartIcon standardizes icon rendering so SVG assets can be colorized via masks,
 * while non-SVG assets still render consistently with the same API.
 */
export function SmartIcon({
  src,
  size,
  alt = '',
  color,
  className,
  dimmed,
  preserveSvgColor = false,
}: SmartIconProps): JSX.Element {
  const useMask = isSvg(src) && !preserveSvgColor;

  const opacity = dimmed ? 0.5 : 1;

  if (useMask) {
    return (
      <div
        role="img"
        aria-label={alt}
        className={className}
        style={{
          width: size,
          height: size,
          opacity,
          backgroundColor: color,
          WebkitMaskImage: `url(${src})`,
          WebkitMaskRepeat: 'no-repeat',
          WebkitMaskSize: 'contain',
          WebkitMaskPosition: 'center',
          maskImage: `url(${src})`,
          maskRepeat: 'no-repeat',
          maskSize: 'contain',
          maskPosition: 'center',
          display: 'block',
        }}
      />
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={{
        width: size,
        height: size,
        display: 'block',
        opacity,
      }}
    />
  );
}
